import React, { useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";

// ─── Constants ────────────────────────────────────────────────────────────────

const PIN_LENGTH = 4;

// Keypad layout: empty string = invisible spacer
const KEYPAD: ReadonlyArray<string> = [
  "1", "2", "3",
  "4", "5", "6",
  "7", "8", "9",
  "",  "0", "⌫",
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Validate PIN against the API (stub — replace with real call). */
async function validatePin(pin: string): Promise<boolean> {
  // TODO: POST /auth/pin with { pin } and parse response
  // const res = await fetch("/api/auth/pin", {
  //   method: "POST",
  //   headers: { "Content-Type": "application/json" },
  //   body: JSON.stringify({ pin }),
  // });
  // return res.ok;

  // Temporary stub: PIN 1234 is accepted
  return pin === "1234";
}

// ─── Component ────────────────────────────────────────────────────────────────

export function PinGate(): React.ReactElement {
  const [pin, setPin] = useState<string>("");
  const [shakeKey, setShakeKey] = useState<number>(0);
  const [errorMsg, setErrorMsg] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const navigate = useNavigate();

  const handleKey = useCallback(
    async (key: string): Promise<void> => {
      if (loading) return;

      // Backspace
      if (key === "⌫") {
        setPin((prev) => prev.slice(0, -1));
        setErrorMsg("");
        return;
      }

      // Spacer key — ignore
      if (key === "") return;

      const next = pin + key;
      setPin(next);

      if (next.length < PIN_LENGTH) return;

      // PIN complete — validate
      setLoading(true);
      try {
        const valid = await validatePin(next);
        if (valid) {
          navigate("/parent");
        } else {
          setShakeKey((k) => k + 1);
          setErrorMsg("Falscher PIN. Bitte nochmal versuchen.");
          setTimeout(() => {
            setPin("");
            setErrorMsg("");
          }, 700);
        }
      } finally {
        setLoading(false);
      }
    },
    [pin, loading, navigate],
  );

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-8 bg-gray-50 p-6">
      {/* Icon + heading */}
      <motion.div
        initial={{ opacity: 0, scale: 0.85 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ type: "spring", stiffness: 260, damping: 20 }}
        className="flex flex-col items-center gap-2 text-center"
      >
        <span className="text-5xl">🔐</span>
        <h1 className="text-xl font-bold text-gray-800">Eltern-Bereich</h1>
        <p className="text-sm text-gray-500">Gib deinen 4-stelligen PIN ein.</p>
      </motion.div>

      {/* PIN dot indicators */}
      <motion.div
        key={shakeKey}
        animate={
          shakeKey > 0
            ? { x: [0, -8, 8, -8, 8, 0] }
            : { x: 0 }
        }
        transition={{ duration: 0.4 }}
        className="flex gap-4"
      >
        {Array.from({ length: PIN_LENGTH }).map((_, i) => (
          <motion.div
            key={i}
            animate={{
              backgroundColor: pin.length > i ? "#9333ea" : "#e5e7eb",
              scale: pin.length === i + 1 ? [1, 1.25, 1] : 1,
            }}
            transition={{ duration: 0.2 }}
            className="h-4 w-4 rounded-full"
          />
        ))}
      </motion.div>

      {/* Keypad */}
      <div className="grid w-64 grid-cols-3 gap-3">
        {KEYPAD.map((key, idx) => (
          <button
            key={idx}
            onClick={() => { void handleKey(key); }}
            disabled={key === "" || loading}
            className={`flex h-16 items-center justify-center rounded-2xl text-xl font-semibold shadow-sm transition-colors ${
              key === ""
                ? "invisible"
                : key === "⌫"
                ? "bg-gray-200 text-gray-600 active:bg-gray-300"
                : "bg-white text-gray-800 active:bg-purple-50"
            }`}
          >
            {key}
          </button>
        ))}
      </div>

      {/* Error message */}
      <AnimatePresence>
        {errorMsg && (
          <motion.p
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="text-sm font-medium text-red-500"
          >
            {errorMsg}
          </motion.p>
        )}
      </AnimatePresence>

      {/* Loading indicator */}
      <AnimatePresence>
        {loading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="text-sm text-purple-500"
          >
            Wird geprüft…
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
